/**
 * ITAT Programs — live list of rolled-out IT Assessment programs.
 *
 * Provides a "Roll Out" entry point directly in the Programs tab so the user
 * does not need to switch to the ITAT Templates tab. Pattern mirrors
 * ControlPrograms (CAT).
 */
import React, { useCallback, useEffect, useState } from 'react';
import Skeleton from '../common/Skeleton';
import { Search, Calendar, User, Rocket, Plus } from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
import GeographyPicker, { GeographySelection, isGeographyComplete } from '../common/GeographyPicker';
import {
    itaProgramService,
    itaTemplateService,
    ItaProgram,
    ItaTemplate,
    ItaTemplateTarget,
    ItaTargetType,
} from '../../services/itaService';
import ItaCoverageWarningModal from './ItaCoverageWarningModal';
import { userService } from '../../services/userService';
import extractApiError from '../../utils/apiError';
import ITATExecutionModal from './ITATExecutionModal';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedDatePicker } from '../../validation/ValidatedFields';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

// Match RAT Programs (RiskPrograms.tsx) — ITAT surfaces only the three RAT
// states: In-Progress / Submitted / Closed. ITA's DB uses Not-Started (rolled
// out, untouched), Complete (maker submitted) and Approved (checker signed
// off), so map those onto the RAT vocabulary.
const STATUS_LABELS: Record<string, string> = {
    'Not-Started': 'In-Progress',
    Complete: 'Submitted',
    Approved: 'Closed',
};
const statusLabel = (status?: string | null) =>
    STATUS_LABELS[status || ''] || status || 'In-Progress';

const statusBadgeClass = (status?: string | null) => {
    switch (statusLabel(status)) {
        case 'Closed':      return 'bg-green-100 text-green-700';
        case 'Submitted':   return 'bg-purple-100 text-purple-700';
        case 'In-Progress': return 'bg-blue-100 text-blue-700';
        default:            return 'bg-gray-100 text-gray-600';
    }
};

// ----------------------------------------------------------------------------
// Roll Out modal — template picker + maker(optional)/checker/geography/dates.
// ----------------------------------------------------------------------------

const ITARolloutModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onRolledOut: (skipped: { target_type: ItaTargetType; target_id: number; reason: string }[]) => void;
    onError: (msg: string) => void;
}> = ({ isOpen, onClose, onRolledOut, onError }) => {
    const [templates, setTemplates] = useState<ItaTemplate[]>([]);
    const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [templateId, setTemplateId] = useState('');
    const [makerId, setMakerId] = useState('');
    const [checkerId, setCheckerId] = useState('');
    const [geography, setGeography] = useState<GeographySelection>({ mode: 'locations', location_ids: [] });
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [coverageGaps, setCoverageGaps] = useState<ItaTemplateTarget[] | null>(null);
    const [totalTargets, setTotalTargets] = useState(0);
    const vh = useFieldValidation();

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);
        vh.reset();
        setTemplateId(''); setMakerId(''); setCheckerId('');
        setGeography({ mode: 'locations', location_ids: [] }); setStartDate(''); setEndDate('');
        setCoverageGaps(null);
        (async () => {
            try {
                const [tplList, userList] = await Promise.all([
                    itaTemplateService.list(),
                    userService.getUsersDropdown(),
                ]);
                if (cancelled) return;
                // Only roll-out-ready templates: active, approved, and with at least
                // one asset/application in scope. Unconfigured (scope=0) or unapproved
                // templates would fail server-side with "no assets in scope".
                setTemplates((tplList || []).filter(t =>
                    (t.is_active ?? 1) === 1
                    && t.workflow_state === 'approved'
                    && (t.target_count ?? 0) > 0));
                setUsers(userList.map(u => ({ id: u.id, name: u.name })));
            } catch (e: any) {
                if (!cancelled) onError(extractApiError(e, 'Failed to load options'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // Only reset/reload when the modal opens. `onError` is an inline
        // callback recreated on every parent render (e.g. when a toast fires),
        // so including it here would re-run this effect and wipe the user's
        // in-progress inputs whenever an error toast is shown.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        const valid = vh.validateAll([
            { name: 'start_date', code: 305, value: startDate, required: true },
            { name: 'end_date',   code: 305, value: endDate,   required: true },
        ]);
        if (!templateId) { onError('ITAT Template is required.'); return; }
        if (!makerId)    { onError('Maker is required.'); return; }
        if (!checkerId)  { onError('Checker is required.'); return; }
        if (!isGeographyComplete(geography)) { onError('Geography is required.'); return; }
        if (!valid) return;
        if (endDate < startDate)  { vh.setError('end_date', 'End date must be on or after start date.'); return; }
        setSaving(true);
        // Pre-rollout guard: warn when in-scope assets/apps have no controls
        // wired via IT Asset / IT Application implementations — those targets
        // roll out empty (backend skips them). Best-effort: a failed check
        // never blocks the rollout itself.
        try {
            const detail = await itaTemplateService.get(parseInt(templateId, 10));
            const targets = detail?.targets || [];
            const gaps = targets.filter(t => (t.wired_control_count ?? 0) === 0);
            if (gaps.length > 0) {
                setTotalTargets(targets.length);
                setCoverageGaps(gaps);
                setSaving(false);
                return; // user decides in the warning modal
            }
        } catch { /* coverage check is advisory only */ }
        await doRollout();
    };

    const doRollout = async () => {
        setSaving(true);
        try {
            const res = await itaTemplateService.rollout(parseInt(templateId, 10), {
                maker_id: makerId ? parseInt(makerId, 10) : null,
                checker_id: parseInt(checkerId, 10),
                geography,
                start_date: startDate,
                end_date: endDate,
            });
            setCoverageGaps(null);
            onRolledOut(res.skipped || []);
            onClose();
        } catch (err: any) {
            onError(extractApiError(err, 'Failed to roll out program.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={false} data-testid="MOD_ita_rollout">
            <Modal.Header>Roll Out ITAT Program</Modal.Header>
            <Modal.Body>
                <form id="ita-program-rollout-form" onSubmit={submit} className="flex flex-col gap-y-4">
                    {makerId !== '' && makerId === checkerId && (
                        <div className="warning-banner" role="status"
                             data-testid="WRN_rollout_same_maker_checker">
                            Maker and checker are the same person. This assessment will be performed and approved by one individual — permitted, and recorded as self-reviewed.
                        </div>
                    )}
                    <FormControl.SearchSelect
                        label="ITAT Template"
                        required
                        placeholder="Select template…"
                        options={templates.map(t => ({
                            value: t.id,
                            label: `${t.ita_code ? `${t.ita_code} — ` : ''}${t.name || t.ita_code}`,
                        }))}
                        value={templateId}
                        onChange={v => setTemplateId(v != null ? String(v) : '')}
                        disabled={loading}
                        clearable
                        data-testid="SEL_ita_rollout_template"
                    />
                    <FormControl.SearchSelect
                        label="Maker (executor)"
                        required
                        placeholder="Select user…"
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={makerId}
                        onChange={v => setMakerId(v != null ? String(v) : '')}
                        disabled={loading}
                        clearable
                        data-testid="SEL_ita_program_rollout_maker"
                    />
                    <FormControl.SearchSelect
                        label="Checker (approver)"
                        required
                        placeholder="Select user…"
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={checkerId}
                        onChange={v => setCheckerId(v != null ? String(v) : '')}
                        disabled={loading}
                        clearable
                        data-testid="SEL_ita_program_rollout_checker"
                    />
                    <div>
                        <label className="fg__label">Geography <span className="fg__required" aria-hidden="true">*</span></label>
                        <GeographyPicker value={geography} onChange={setGeography} disabled={loading} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-4">
                        <ValidatedDatePicker
                            vh={vh}
                            name="start_date"
                            code={305}
                            required
                            label="Start Date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            data-testid="INP_ita_rollout_start_date"
                        />
                        <ValidatedDatePicker
                            vh={vh}
                            name="end_date"
                            code={305}
                            required
                            label="End Date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            data-testid="INP_ita_rollout_end_date"
                        />
                    </div>
                    <p className="text-xs text-gray-500 italic">
                        Roll-out creates one IT assessment program per in-scope asset / application.
                    </p>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button type="submit" form="ita-program-rollout-form" variant="fill" icon={Rocket} disabled={saving || loading} data-testid="BTN_ita_program_rollout">
                    {saving ? 'Rolling out…' : 'Roll Out'}
                </Button>
            </Modal.Footer>
            <ItaCoverageWarningModal
                isOpen={coverageGaps !== null}
                onClose={() => setCoverageGaps(null)}
                gaps={coverageGaps || []}
                totalTargets={totalTargets}
                templateName={templates.find(t => String(t.id) === templateId)?.name || undefined}
                proceeding={saving}
                onProceed={doRollout}
            />
        </Modal>
    );
};

// ----------------------------------------------------------------------------
// Main listing
// ----------------------------------------------------------------------------

const ITATPrograms: React.FC = () => {
    const { formatDate } = useEntitySettings();
    const [rows, setRows] = useState<ItaProgram[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [openId, setOpenId] = useState<number | null>(null);
    const [isRolloutOpen, setIsRolloutOpen] = useState(false);
    const { check } = usePermissions();
    // ITA_PROGRAMS view = the gate for this tab; without it the user sees the
    // "Not authorised" screen. The view+modify extent additionally marks a
    // supervisor who may Roll Out and sees every program; a plain "view" holder is
    // execution-only and sees just the programs rolled out to them (maker/checker).
    const { allowed: canView, loading: viewLoading } = usePermission('ITA_PROGRAMS', 'view');
    const canModify = check('ITA_PROGRAMS', 'view+modify').allowed;

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await itaProgramService.list({ search: search || undefined, mine: !canModify });
            setRows(data);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load programs') });
        } finally {
            setLoading(false);
        }
    }, [search, canModify]);

    useEffect(() => { if (canView) refresh(); }, [refresh, canView]);
    useEffect(() => { setPage(1); }, [search, pageSize]);

    const paged = rows.slice((page - 1) * pageSize, page * pageSize);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to 'ITA_PROGRAMS'." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search programs…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_ita_programs_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={() => setIsRolloutOpen(true)} data-testid="BTN_ita_programs_rollout">Roll Out</Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Program</th>
                                        <th className="table-list-th">Template</th>
                                        <th className="table-list-th">Asset / Application</th>
                                        <th className="table-list-th">Geography</th>
                                        <th className="table-list-th">Window</th>
                                        <th className="table-list-th">Maker</th>
                                        <th className="table-list-th">Checker</th>
                                        <th className="table-list-th table-list-th--right">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'sm', 'sm', 'sm', 'sm', 'sm', { width: 'pill', align: 'right' }]} />}
                                    {!loading && paged.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="table-list-cell">
                                                <NoDataFound
                                                    title={search ? 'No Matching Programs' : 'No ITAT Programs Yet'}
                                                    message={search ? 'No programs match your search.' : 'Click "Roll Out" to launch one from an ITAT Template.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paged.map((p, idx) => (
                                        <tr
                                            key={p.id}
                                            className="table-list-row"
                                            onClick={() => setOpenId(p.id)}
                                            style={{ cursor: 'pointer' }}
                                            data-testid={`BTN_ita_program_row_${p.id}_open`}
                                        >
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">{p.program_name}</span>
                                            </td>
                                            <td className="table-list-cell">{p.template_name || p.template_code || '—'}</td>
                                            <td className="table-list-cell">
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                                    {p.template_target_count ?? 0} asset(s)/app(s)
                                                </span>
                                            </td>
                                            <td className="table-list-cell">{p.location_name || ''}</td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-xs text-gray-600">
                                                    <Calendar size={12} className="text-gray-400" />
                                                    {formatDate(p.start_date)} → {formatDate(p.end_date)}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-sm">
                                                    <User size={12} className="text-gray-400" />
                                                    {p.maker_name || '—'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-sm">
                                                    <User size={12} className="text-gray-400" />
                                                    {p.checker_name || '—'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(p.status)}`} style={{ whiteSpace: 'nowrap' }}>
                                                    {statusLabel(p.status)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={rows.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="programs"
                        />
                    </div>
                </div>
            </div>

            <ITARolloutModal
                isOpen={isRolloutOpen}
                onClose={() => setIsRolloutOpen(false)}
                onRolledOut={(skipped) => {
                    const skipNote = skipped.length ? ` (${skipped.length} skipped)` : '';
                    setToast({ type: 'success', message: `ITAT Program(s) rolled out.${skipNote}` });
                    refresh();
                }}
                onError={(msg) => setToast({ type: 'error', message: msg })}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />

            {openId !== null && (
                <ITATExecutionModal
                    programId={openId}
                    onClose={() => setOpenId(null)}
                    readOnly
                />
            )}
        </div>
    );
};

export default ITATPrograms;
